import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, XCircle, Info, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/useToast";
import type { ToastType } from "@/context/NotificationContext";

const icons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const styles: Record<ToastType, string> = {
  success: "border-success/30 bg-success/5",
  error: "border-danger/30 bg-danger/5",
  info: "border-accent/30 bg-accent/5",
  warning: "border-warning/30 bg-warning/5",
};

const iconColors: Record<ToastType, string> = {
  success: "text-success",
  error: "text-danger",
  info: "text-accent",
  warning: "text-warning",
};

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  return (
    <div
      role="region"
      aria-label="Notifications"
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2"
    >
      <AnimatePresence>
        {toasts.map((toast) => {
          const Icon = icons[toast.type];
          const isAlert = toast.type === "error" || toast.type === "warning";
          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              role={isAlert ? "alert" : "status"}
              aria-live={isAlert ? "assertive" : "polite"}
              aria-atomic="true"
              className={cn(
                "flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg bg-surface min-w-[300px] max-w-[420px]",
                styles[toast.type]
              )}
            >
              <Icon
                className={cn("h-5 w-5 shrink-0", iconColors[toast.type])}
                aria-hidden="true"
              />
              <p className="text-sm text-text-primary flex-1">{toast.message}</p>
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                aria-label="Dismiss notification"
                className="text-text-muted hover:text-text-primary rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
